import {
  type AnalysisBuffer,
  type PixelBuffer,
  createLumaPlane,
} from '../types/image.js';
import { clamp } from '../types/geometry.js';

/**
 * Prepares a small working buffer for the pixel-based stages.
 *
 * Background and quality analysis do not need the full 12-megapixel upload:
 * downscaling to <= ~720 px on the long side keeps the whole deep stage in the
 * low single-digit milliseconds on a phone, which is what makes the
 * "fast result first, deep result second" UX possible.
 *
 * The downscale is a box filter computed on the fly (no intermediate buffers),
 * so a 4000x3000 photo costs one pass and one small allocation.
 */
export const createAnalysisBuffer = (source: PixelBuffer, maxSide = 720): AnalysisBuffer => {
  const { width, height, channels, data } = source;
  if (width <= 0 || height <= 0) {
    const empty = new Uint8ClampedArray(0);
    return { data: empty, width: 0, height: 0, channels: 1, scale: 1, luma: empty };
  }

  const longest = Math.max(width, height);
  if (longest <= maxSide) {
    const luma = createLumaPlane(source);
    if (channels === 1) {
      return { data, width, height, channels: 1, scale: 1, luma };
    }
    return { data, width, height, channels, scale: 1, luma };
  }

  const scale = maxSide / longest;
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));
  const keepColor = channels >= 3;
  const outChannels = keepColor ? 3 : 1;
  const out = new Uint8ClampedArray(targetWidth * targetHeight * outChannels);
  const luma = new Uint8ClampedArray(targetWidth * targetHeight);

  const blockWidth = width / targetWidth;
  const blockHeight = height / targetHeight;

  for (let ty = 0; ty < targetHeight; ty += 1) {
    const y0 = Math.floor(ty * blockHeight);
    const y1 = Math.min(height, Math.max(y0 + 1, Math.floor((ty + 1) * blockHeight)));
    for (let tx = 0; tx < targetWidth; tx += 1) {
      const x0 = Math.floor(tx * blockWidth);
      const x1 = Math.min(width, Math.max(x0 + 1, Math.floor((tx + 1) * blockWidth)));
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let sumLuma = 0;
      let count = 0;
      for (let y = y0; y < y1; y += 1) {
        let index = (y * width + x0) * channels;
        for (let x = x0; x < x1; x += 1) {
          if (channels === 1) {
            const value = data[index] as number;
            sumR += value;
            sumG += value;
            sumB += value;
            sumLuma += value;
          } else {
            const r = data[index] as number;
            const g = data[index + 1] as number;
            const b = data[index + 2] as number;
            sumR += r;
            sumG += g;
            sumB += b;
            sumLuma += 0.299 * r + 0.587 * g + 0.114 * b;
          }
          index += channels;
          count += 1;
        }
      }
      const target = ty * targetWidth + tx;
      if (keepColor) {
        out[target * 3] = count > 0 ? sumR / count : 0;
        out[target * 3 + 1] = count > 0 ? sumG / count : 0;
        out[target * 3 + 2] = count > 0 ? sumB / count : 0;
      } else {
        out[target] = count > 0 ? sumR / count : 0;
      }
      luma[target] = count > 0 ? sumLuma / count : 0;
    }
  }

  return {
    data: out,
    width: targetWidth,
    height: targetHeight,
    channels: outChannels as 1 | 3,
    scale: targetWidth / width,
    luma,
  };
};

/** Reads one luma sample with edge clamping. */
export const sampleLuma = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): number => {
  const cx = clamp(Math.round(x), 0, width - 1);
  const cy = clamp(Math.round(y), 0, height - 1);
  return luma[cy * width + cx] as number;
};

/** Mean luma inside a normalized rectangle. */
export const meanLumaInRect = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: { x: number; y: number; width: number; height: number },
): number => {
  const x0 = clamp(Math.floor(rect.x * width), 0, width - 1);
  const x1 = clamp(Math.ceil((rect.x + rect.width) * width), x0 + 1, width);
  const y0 = clamp(Math.floor(rect.y * height), 0, height - 1);
  const y1 = clamp(Math.ceil((rect.y + rect.height) * height), y0 + 1, height);
  let sum = 0;
  let count = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      sum += luma[y * width + x] as number;
      count += 1;
    }
  }
  return count > 0 ? sum / count : 0;
};

/** Downscale-friendly RGB read used by the background analyser. */
export const rgbAt = (
  data: Uint8ClampedArray,
  width: number,
  channels: number,
  x: number,
  y: number,
): { r: number; g: number; b: number } => {
  const index = (y * width + x) * channels;
  if (channels === 1) {
    const value = data[index] as number;
    return { r: value, g: value, b: value };
  }
  return {
    r: data[index] as number,
    g: data[index + 1] as number,
    b: data[index + 2] as number,
  };
};

/**
 * Cheap estimate of the background luma: the average of four border strips.
 *
 * Used by the silhouette head-top refinement, which needs a "what does the
 * background look like" reference *before* the full background stage runs — so
 * that face geometry can be finalised while the user is still looking at the
 * fast result.
 */
export const ringLuma = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  thickness = 0.06,
): number => {
  const strips = [
    { x: 0, y: 0, width: 1, height: thickness },
    { x: 0, y: 1 - thickness, width: 1, height: thickness },
    { x: 0, y: 0, width: thickness, height: 1 },
    { x: 1 - thickness, y: 0, width: thickness, height: 1 },
  ];
  const values = strips
    .map((strip) => meanLumaInRect(luma, width, height, strip))
    .filter((value) => Number.isFinite(value));
  if (values.length === 0) return 128;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

/**
 * Background luma of the **top band only**.
 *
 * The silhouette scan looks for the top of the head, so its reference must
 * describe the background *above* the head. The four-strip ring average is dragged
 * down by whatever sits at the bottom of a portrait — dark clothing and shoulders —
 * and on real photographs that offset (measured: ring 184 vs actual 254 on
 * `tests/geometry/real/images/01.png`) made every background pixel read as
 * "subject": no edge was ever found, and the hair-inclusive head top could not be
 * measured. The scan receives both references and treats a pixel as background
 * when it matches *either* of them.
 */
export const topBandLuma = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  thickness = 0.06,
): number => {
  const value = meanLumaInRect(luma, width, height, { x: 0, y: 0, width: 1, height: thickness });
  return Number.isFinite(value) ? value : 128;
};
